import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useState } from 'react'
import { useEditOrder } from '@/api/hooks'
import type { OrderDetail } from '@/api/types'
import { ErrorBanner } from '@/components/common'

const EVENT_TYPES = [
  'WEDDING', 'RECEPTION', 'ENGAGEMENT', 'BIRTHDAY', 'CORPORATE',
  'CONFERENCE', 'HOUSE_WARMING', 'NAMING_CEREMONY', 'OTHER',
]

/**
 * Corrects the details of an order.
 *
 * <p>Only the things that get written down wrongly: who to call, who is running
 * it, what the kitchen was told. The date, the hall and the head count are what
 * the customer agreed to and move through a change request, which is why they
 * are not on this form.</p>
 */
export function OrderEditDialog(props: { order: OrderDetail; open: boolean; onClose: () => void }) {
  const { order } = props
  const edit = useEditOrder()

  const [eventType, setEventType] = useState(order.eventType ?? '')
  const [contactPersonName, setContactPersonName] = useState(order.contactPersonName ?? '')
  const [contactPhone, setContactPhone] = useState(order.contactPhone ?? '')
  const [specialInstructions, setSpecialInstructions] = useState(order.specialInstructions ?? '')
  const [notes, setNotes] = useState(order.notes ?? '')

  const submit = () =>
    edit.mutate(
      {
        id: order.id,
        body: {
          eventType: eventType || undefined,
          contactPersonName,
          contactPhone,
          specialInstructions,
          notes,
        },
      },
      { onSuccess: props.onClose },
    )

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Correct {order.orderNumber}</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={edit.error} />
        <Alert severity="info" sx={{ mb: 2 }}>
          The date, the hall and the head count are what the customer agreed to — those move through a
          change request. A session's hall and hours can be moved from the Sessions tab.
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Occasion"
              value={eventType}
              onChange={(event) => setEventType(event.target.value)}
            >
              {EVENT_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {value.replace(/_/g, ' ').toLowerCase()}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Contact person"
              value={contactPersonName}
              onChange={(event) => setContactPersonName(event.target.value)}
              helperText="Who to call on the day"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Contact phone"
              value={contactPhone}
              onChange={(event) => setContactPhone(event.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Special instructions"
              value={specialInstructions}
              onChange={(event) => setSpecialInstructions(event.target.value)}
              multiline
              minRows={2}
              helperText="Goes to the kitchen and the service team"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              multiline
              minRows={2}
            />
          </Grid>
        </Grid>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Every correction is recorded against the order.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={edit.isPending}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
